/* eslint-disable no-inline-comments */
/* eslint-disable line-comment-position */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { Panel } from '../panel.class';
import { PanelConnectionType } from '../enums';
import { Axis, Vertex3 } from '../../../spatial-classes';

/**
 * Options for calculating the corner points of a panel.
 */
export type GetPanelCenterOptions = {
	/**
	 * If true, the resulting corner points will be rounded to the nearest sixteenth of a unit.
	 * This is typically used to ensure measurements conform to standard increments.
	 * If false, the points are left as calculated.
	 */
	nearestSixteenth?: boolean;

	/**
	 * If true, the resulting corner points will be read only.
	 * This is useful for preventing accidental modification of the points.
	 */
	lock?: boolean;
}

export const getPanelCenter = (
	panel: Panel,
	options: GetPanelCenterOptions,
	visitedPanels: Set<string> = new Set()
): Vertex3 => {
	// --- Parameter Extraction and Defaults ---
	// Destructure the main calculation options from params, providing defaults for optional values.
	const { nearestSixteenth = true, lock = false } = options;

	// --- Infinite Recursion Prevention ---
	// If this panel has already been visited in the current calculation chain, a circular reference exists.
	// To prevent infinite recursion, log a warning and return null.
	if (visitedPanels.has(panel.id)) {
		console.warn(`Circular connection detected in panel ${panel.id}, returning null for getCornerPoints`);
		return null;
	}
	visitedPanels.add(panel.id);

	// --- Center Point Initialization (default) ---
	// initialize panel center at the middle of the panel height, bottom of panel is always 0
	const panelCenter = new Vertex3({ dataIn: { x: 0, y: panel.size.height / -2, z: 0 }});
	const panels = panel.refMap?.island?.panels ?? null;

	// stand alone penel or front panel, center is default
	if (!panels || panel.isFrontPanel) return panelCenter;

	// --- Find Adjacent Panels ---
	// Retrieve the panel to the left of the current panel, if it exists.
	const leftPanel = panel.connections.left ? panels.findById(panel.connections.left.targetPanelId) : null;

	// no left panel, return center default
	if (!leftPanel) return panelCenter;

	// Recursively get the left panel's corner points (using inside=true for correct alignment).
	// start a new Set because we are recursively getting points with a different parameter set
	const leftPanelCenter = getPanelCenter(leftPanel, { nearestSixteenth: false }, visitedPanels);

	const rightConnectionAxis = new Axis({ dataIn: { y: leftPanel.axis.y + (leftPanel.connections.right.type === PanelConnectionType.Outside ? -90 : 90) }});
	const leftConnectionAxis = new Axis({ dataIn: { y: panel.axis.y + (panel.connections.left.type === PanelConnectionType.Outside ? 90 : -90) }});

	// move to right edge of left panel
	panelCenter
		// move from left panel center to connection point
		.moveTo(leftPanelCenter)
		.moveOnAxis(leftPanel.size.width / 2, new Axis({ dataIn: { y: leftPanel.axis.y }}))
		.moveOnAxis(leftPanel.size.depth / 2, rightConnectionAxis);

	// console.log('getPanelCenter: 1', panel.label, panelCenter.roundNearestSixteenth().serialize());

	panelCenter
		// move from connection point to panel center
		.moveOnAxis(panel.size.depth / 2, leftConnectionAxis)
		.moveOnAxis(panel.size.width / 2, new Axis({ dataIn: { y: panel.getOriginAngle() }}));
	// console.log('getPanelCenter: 2t', panel.label, panelCenter.roundNearestSixteenth().serialize());

	// round the points to the nearest sixteenth if required
	if (nearestSixteenth) panelCenter.roundNearestSixteenth();

	// if requested, lock the points to prevent modification
	if (lock) panelCenter.readOnly = true;

	// console.log('getPanelCenter', visitedPanels.size, panel.label, panelCenter.serialize(), {
	// 	panel: { size: panel.size }
	// });

	// --- Return Result ---
	// Return the calculated corners and center position for the panel.
	return panelCenter;
}
